import type {
  AddMemberInput,
  Agent,
  AgentListItem,
  ApiPreview,
  CreateAgentInput,
  CreateTenantInput,
  CreateKnowledgeSourceInput,
  EntitlementsView,
  KnowledgeDocument,
  KnowledgeSource,
  KnowledgeSourceDetail,
  KnowledgeUsage,
  Me,
  Member,
  Page,
  PlanId,
  Run,
  RunEvent,
  TenantAdminView,
  Tool,
  UploadTicket,
  UpdateAgentInput,
} from '@agents/shared';
import { RUN_EVENT_TYPES, TERMINAL_RUN_STATUSES } from '@agents/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { tenantSelection } from './api';
import { useServices } from './context';
import { readSse } from './sse';

const tenantKey = () => tenantSelection.get() ?? 'default';

export const isTerminal = (status: Run['status']) =>
  (TERMINAL_RUN_STATUSES as readonly string[]).includes(status);

export function useMe() {
  const { api } = useServices();
  return useQuery({ queryKey: ['me', tenantKey()], queryFn: () => api.request<Me>('/me') });
}

export function useAgents() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'agents'],
    queryFn: () => api.request<Page<AgentListItem>>('/agents').then((p) => p.items),
  });
}

export function useAgent(id: string) {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'agent', id],
    queryFn: () => api.request<Agent>(`/agents/${id}`),
  });
}

export function useModels() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'models'],
    queryFn: () => api.request<{ items: string[]; default: string }>('/models'),
    staleTime: 5 * 60_000,
  });
}

export function useTools() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'tools'],
    queryFn: () => api.request<Page<Tool>>('/tools').then((p) => p.items),
    staleTime: 60_000,
  });
}

export function useSaveAgent() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id?: string;
      input: CreateAgentInput | UpdateAgentInput;
      toolIds?: string[];
      knowledgeSourceIds?: string[];
    }) => {
      let agent = args.id
        ? await api.request<Agent>(`/agents/${args.id}`, { method: 'PATCH', body: args.input })
        : await api.request<Agent>('/agents', { method: 'POST', body: args.input });
      if (args.toolIds) {
        agent = await api.request<Agent>(`/agents/${agent.id}/tools`, {
          method: 'PUT',
          body: { toolIds: args.toolIds },
        });
      }
      const knowledge = args.knowledgeSourceIds;
      const changed =
        knowledge &&
        JSON.stringify([...knowledge].sort()) !== JSON.stringify([...agent.knowledgeSourceIds].sort());
      if (knowledge && changed) {
        agent = await api.request<Agent>(`/agents/${agent.id}/knowledge`, {
          method: 'PUT',
          body: { sourceIds: knowledge },
        });
      }
      return agent;
    },
    onSuccess: (agent) => {
      qc.setQueryData([tenantKey(), 'agent', agent.id], agent);
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'agents'] });
    },
  });
}

export function usePublishAgent(id: string) {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.request<Agent>(`/agents/${id}/publish`, { method: 'POST' }),
    onSuccess: (agent) => {
      qc.setQueryData([tenantKey(), 'agent', id], agent);
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'agents'] });
    },
  });
}

export function useRuns(agentId?: string) {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'runs', agentId ?? 'all'],
    queryFn: () =>
      api
        .request<Page<Run>>(agentId ? `/agents/${agentId}/runs?limit=20` : '/runs?limit=50')
        .then((p) => p.items),
    refetchInterval: (q) => (q.state.data?.some((r) => !isTerminal(r.status)) ? 3000 : false),
  });
}

export function useRun(id: string, opts: { poll?: boolean } = {}) {
  const { api } = useServices();
  const poll = opts.poll ?? true;
  return useQuery({
    queryKey: [tenantKey(), 'run', id],
    queryFn: () => api.request<Run>(`/runs/${id}`),
    refetchInterval: (q) => (poll && q.state.data && !isTerminal(q.state.data.status) ? 1500 : false),
  });
}

/** Merge by seq: overlapping fetches and stream + polling must never duplicate events. */
export function mergeEvents(current: RunEvent[] | undefined, incoming: RunEvent[]): RunEvent[] {
  const bySeq = new Map((current ?? []).map((e) => [e.seq, e]));
  for (const e of incoming) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

const runEventsKey = (id: string) => [tenantKey(), 'run-events', id];

/** Incremental event feed (polling): fetches only events after the last cached sequence number. */
export function useRunEvents(id: string, poll: boolean) {
  const { api } = useServices();
  const qc = useQueryClient();
  return useQuery({
    queryKey: runEventsKey(id),
    queryFn: async () => {
      const current = qc.getQueryData<RunEvent[]>(runEventsKey(id));
      const after = current?.at(-1)?.seq ?? 0;
      const page = await api.request<Page<RunEvent>>(`/runs/${id}/events?after=${after}`);
      return mergeEvents(qc.getQueryData<RunEvent[]>(runEventsKey(id)), page.items);
    },
    refetchInterval: poll ? 1500 : false,
  });
}

/**
 * Live events over SSE while the run is active. Reconnects from the last id when the server
 * closes the connection; reports `connected=false` on failure so callers fall back to polling.
 */
export function useRunStream(id: string, active: boolean, onConnectedChange: (connected: boolean) => void) {
  const { api } = useServices();
  const qc = useQueryClient();

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    let lastId = qc.getQueryData<RunEvent[]>(runEventsKey(id))?.at(-1)?.seq ?? 0;
    let ended = false;

    (async () => {
      for (let attempt = 0; !controller.signal.aborted && !ended && attempt < 20; attempt++) {
        try {
          const res = await api.stream(`/runs/${id}/stream`, {
            signal: controller.signal,
            lastEventId: lastId,
          });
          if (!res.ok) throw new Error(`stream ${res.status}`);
          onConnectedChange(true);
          await readSse(
            res,
            (m) => {
              if (m.event === 'end') {
                ended = true;
                return;
              }
              if (!(RUN_EVENT_TYPES as readonly string[]).includes(m.event)) return;
              const event = JSON.parse(m.data) as RunEvent;
              lastId = event.seq;
              qc.setQueryData<RunEvent[]>(runEventsKey(id), (cur) => mergeEvents(cur, [event]));
            },
            controller.signal,
          );
        } catch {
          if (controller.signal.aborted) return;
          onConnectedChange(false);
          return; // fall back to polling
        }
      }
      if (controller.signal.aborted) return;
      // Finished (or gave up reconnecting): refresh the run and let polling take over if needed.
      onConnectedChange(false);
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'run', id] });
    })();

    return () => {
      controller.abort();
      onConnectedChange(false);
    };
  }, [id, active, api, qc, onConnectedChange]);
}

export function useCreateRun(agentId: string) {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string) =>
      api.request<Run>(`/agents/${agentId}/runs`, { method: 'POST', body: { input } }),
    onSuccess: (run) => {
      qc.setQueryData([tenantKey(), 'run', run.id], run);
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'runs'] });
    },
  });
}

export function useCancelRun(id: string) {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.request<Run>(`/runs/${id}/cancel`, { method: 'POST' }),
    onSuccess: (run) => {
      qc.setQueryData([tenantKey(), 'run', id], run);
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'runs'] });
      void qc.invalidateQueries({ queryKey: [tenantKey(), 'run-events', id] });
    },
  });
}

export function useMembers() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'members'],
    queryFn: () => api.request<Page<Member>>('/tenants/current/members').then((p) => p.items),
  });
}

export function useAddMember() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMemberInput) =>
      api.request<Page<Member>>('/tenants/current/members', { method: 'POST', body: input }),
    onSuccess: (page) => qc.setQueryData([tenantKey(), 'members'], page.items),
  });
}

export function useRemoveMember() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.request<void>(`/tenants/current/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [tenantKey(), 'members'] }),
  });
}

export function useTenants() {
  const { api } = useServices();
  return useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => api.request<Page<TenantAdminView>>('/tenants').then((p) => p.items),
  });
}

export function useCreateTenant() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantInput) =>
      api.request<{ id: string; name: string; slug: string }>('/tenants', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useEntitlements() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'entitlements'],
    queryFn: () => api.request<EntitlementsView>('/entitlements'),
    staleTime: 60_000,
  });
}

export function useTenantEntitlements(tenantId: string | null) {
  const { api } = useServices();
  return useQuery({
    queryKey: ['platform', 'tenant-entitlements', tenantId],
    queryFn: () => api.request<EntitlementsView>(`/tenants/${tenantId}/entitlements`),
    enabled: !!tenantId,
  });
}

export function useSetTenantPlan() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; plan: PlanId }) =>
      api.request<{ id: string; plan: PlanId }>(`/tenants/${args.tenantId}/plan`, {
        method: 'PATCH',
        body: { plan: args.plan },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useSetTenantOverrides() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; reason: string; overrides: Record<string, unknown> }) =>
      api.request(`/tenants/${args.tenantId}/entitlements`, {
        method: 'PUT',
        body: { reason: args.reason, overrides: args.overrides },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform'] }),
  });
}

/* ------------------------------------------------------------------ Knowledge */

const PENDING_DOCUMENT = new Set(['uploaded', 'scanning', 'processing']);
export const isDocumentPending = (d: KnowledgeDocument) => PENDING_DOCUMENT.has(d.status);

export function useKnowledgeSources(enabled = true) {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'knowledge', 'sources'],
    queryFn: () => api.request<Page<KnowledgeSource>>('/knowledge/sources').then((p) => p.items),
    enabled,
  });
}

export function useKnowledgeSource(id: string) {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'knowledge', 'source', id],
    queryFn: () => api.request<KnowledgeSourceDetail>(`/knowledge/sources/${id}`),
    // Documents move through scanning and processing on their own; refresh until they settle.
    refetchInterval: (q) =>
      q.state.data?.status === 'syncing' || q.state.data?.documents.some(isDocumentPending) ? 2500 : false,
  });
}

export function useKnowledgeUsage() {
  const { api } = useServices();
  return useQuery({
    queryKey: [tenantKey(), 'knowledge', 'usage'],
    queryFn: () => api.request<KnowledgeUsage>('/knowledge/usage'),
  });
}

function useInvalidateKnowledge() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [tenantKey(), 'knowledge'] });
}

export function useCreateKnowledgeSource() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (input: CreateKnowledgeSourceInput) =>
      api.request<KnowledgeSource>('/knowledge/sources', { method: 'POST', body: input }),
    onSuccess: () => void invalidate(),
  });
}

export function useSyncKnowledgeSource() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (id: string) =>
      api.request<KnowledgeSource>(`/knowledge/sources/${id}/sync`, { method: 'POST' }),
    onSuccess: () => void invalidate(),
  });
}

export function useUpdateKnowledgeSource() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (args: { id: string; patch: Record<string, unknown> }) =>
      api.request<KnowledgeSource>(`/knowledge/sources/${args.id}`, { method: 'PATCH', body: args.patch }),
    onSuccess: () => void invalidate(),
  });
}

export function usePreviewConnector() {
  const { api } = useServices();
  return useMutation({
    mutationFn: (body: { api: unknown; apiSecret?: string; sourceId?: string }) =>
      api.request<ApiPreview>('/knowledge/connectors/preview', { method: 'POST', body }),
  });
}

export function useDeleteKnowledgeSource() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (id: string) => api.request<void>(`/knowledge/sources/${id}`, { method: 'DELETE' }),
    onSuccess: () => void invalidate(),
  });
}

export function useAddKnowledgeText(sourceId: string) {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (input: { title: string; content: string }) =>
      api.request<KnowledgeDocument>(`/knowledge/sources/${sourceId}/documents/text`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => void invalidate(),
  });
}

/** Ticket (plan limits are enforced here) -> direct upload -> confirm. */
export function useUploadKnowledgeFile(sourceId: string) {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async (file: File) => {
      const ticket = await api.request<UploadTicket>(`/knowledge/sources/${sourceId}/uploads`, {
        method: 'POST',
        body: { fileName: file.name, sizeBytes: file.size },
      });
      try {
        await api.upload(ticket.url, ticket.fields, file);
      } catch (err) {
        // Frees the reserved space right away instead of waiting for the ticket to expire.
        await api.request(`/knowledge/documents/${ticket.document.id}`, { method: 'DELETE' }).catch(() => {});
        throw err;
      }
      return api.request<KnowledgeDocument>(`/knowledge/documents/${ticket.document.id}/complete`, {
        method: 'POST',
      });
    },
    onSettled: () => void invalidate(),
  });
}

export function useRetryKnowledgeDocument() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (id: string) =>
      api.request<KnowledgeDocument>(`/knowledge/documents/${id}/retry`, { method: 'POST' }),
    onSuccess: () => void invalidate(),
  });
}

export function useDeleteKnowledgeDocument() {
  const { api } = useServices();
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (id: string) => api.request<void>(`/knowledge/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => void invalidate(),
  });
}
